import classNames from "classnames";
import { ConditionalLink } from "components/conditionalLink";
import { ArrowUpRight } from "components/icons/ArrowUpRight";
import { FilledGreenTick } from "components/icons/FilledGreenTick";
import { useToast } from "hooks/useToast";
import { useLocation } from "react-router-dom";
import { useQuickReplyToMessageMutation } from "store/apis/chat";
import { useChannelStateContext } from "stream-chat-react";

export const QuickReplyButton = ({
  label,
  value,
  messageId,
  sentByTheUser,
  selectedValue,
  redirectType,
  taskId,
  type,
}: {
  label: string;
  value?: string;
  type: "text" | "redirect";
  redirectType?: "CART" | "TASK" | "CONNECTION";
  taskId?: string;
  messageId: string;
  sentByTheUser: boolean;
  selectedValue?: string;
}) => {
  const [quickReply] = useQuickReplyToMessageMutation();
  const { alertToast, successToast } = useToast();
  const { channel } = useChannelStateContext();
  const location = useLocation();

  const groupId = channel?.data?.group_uuid || "";
  const entityId = channel?.data?.entity_id || "";

  const handleQuickReply = async () => {
    if (channel.id && value) {
      try {
        await quickReply({
          payload: {
            selected_option: value,
            message_id: messageId,
            task_id: taskId,
          },
          channelId: channel.id!,
        }).unwrap();
        successToast({ message: "Response sent successfully" });
      } catch (error) {
        alertToast({
          message:
            (error as any)?.data?.error?.message || "Failed to send response",
        });
      }
    }
  };

  const constructRedirectUrl = () => {
    if (type !== "redirect") {
      return "";
    }

    if (redirectType === "TASK" && taskId) {
      return `/tax/filings/${taskId}`;
    }

    if (redirectType === "CART") {
      return `/settings/billing/cart?entity=${entityId}&settings_back_to=${encodeURIComponent(
        location.pathname + location.search
      )}`;
    }

    if (redirectType === "CONNECTION") {
      return `/books/data-sources?entity=${entityId}`;
    }

    return "";
  };

  return (
    <ConditionalLink
      to={constructRedirectUrl()}
      className="t-w-full t-text-purple t-block"
    >
      <button
        onClick={handleQuickReply}
        disabled={Boolean(selectedValue)}
        className={classNames(
          "t-p-2 t-rounded-lg t-border t-border-solid t-border-surface-transparent t-text-body-sm t-transition-colors t-bg-white all:unset t-text-center t-w-full t-relative",
          {
            "t-text-purple hover:t-bg-purple-0 active:t-border-purple":
              !Boolean(selectedValue),
            "t-cursor-default": Boolean(selectedValue),
            "disabled:!t-text-neutral-40": selectedValue !== value,
          }
        )}
      >
        <div className="t-flex t-items-center t-justify-center t-gap-2">
          <span>{label}</span>

          {type === "redirect" && (
            <span className="t-flex">
              <ArrowUpRight color="currentColor" />
            </span>
          )}
        </div>
        {selectedValue === value && (
          <span className="t-text-dark_green-50 t-inline-block t-absolute -t-right-2 t-top-1/2 -t-translate-y-1/2 t-translate-x-full">
            <FilledGreenTick width="20" height="20" />
          </span>
        )}
      </button>
    </ConditionalLink>
  );
};
